import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router';

import { AccountLayout } from '@/components/layout/AccountLayout';
import { AppLayout } from '@/components/layout/AppLayout';
import { FullPageLoader } from '@/components/ui/FullPageLoader';

import { RequireAuth, RequireGuest, RequireOnboarding } from './guards';
import { routes } from './routes';

/*
 * Routes are code-split per screen. The workout player in particular must not
 * carry the chart bundle, and a first-time visitor should not download the
 * dashboard to see a login form.
 */
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const WorkoutsPage = lazy(() => import('@/pages/workouts/WorkoutsPage'));
const WorkoutDetailPage = lazy(() => import('@/pages/workouts/WorkoutDetailPage'));
const BuilderPage = lazy(() => import('@/pages/builder/BuilderPage'));
const ProgrammesPage = lazy(() => import('@/pages/programmes/ProgrammesPage'));
const ProgrammeDetailPage = lazy(() => import('@/pages/programmes/ProgrammeDetailPage'));
const ProgrammeBuilderPage = lazy(() => import('@/pages/programmes/ProgrammeBuilderPage'));
const ExercisesPage = lazy(() => import('@/pages/exercises/ExercisesPage'));
const ExerciseDetailPage = lazy(() => import('@/pages/exercises/ExerciseDetailPage'));
const PlayerPage = lazy(() => import('@/pages/player/PlayerPage'));
const SessionSummaryPage = lazy(() => import('@/pages/player/SessionSummaryPage'));
const MeasurementsPage = lazy(() => import('@/pages/measurements/MeasurementsPage'));
const ProgressPage = lazy(() => import('@/pages/progress/ProgressPage'));
const HistoryPage = lazy(() => import('@/pages/history/HistoryPage'));
const EquipmentPage = lazy(() => import('@/pages/equipment/EquipmentPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const RemindersSettingsPage = lazy(() => import('@/pages/settings/RemindersSettingsPage'));
const IntegrationsSettingsPage = lazy(() => import('@/pages/settings/IntegrationsSettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

export function App() {
    return (
        <Suspense fallback={<FullPageLoader />}>
            <Routes>
                {/* Reachable while signed out; a signed-in visitor is sent home. */}
                <Route element={<RequireGuest />}>
                    <Route path={routes.login} element={<LoginPage />} />
                    <Route path={routes.register} element={<RegisterPage />} />
                    <Route path={routes.forgotPassword} element={<ForgotPasswordPage />} />
                    <Route path={routes.resetPassword} element={<ResetPasswordPage />} />
                </Route>

                {/* Reachable either way: the link is opened from an e-mail. */}
                <Route path={routes.verifyEmail} element={<VerifyEmailPage />} />

                <Route element={<RequireAuth />}>
                    {/* The intake sits outside the app shell: it is a focused,
                        full-screen flow with its own header and footer. */}
                    <Route path={routes.onboarding} element={<OnboardingPage />} />

                    {/* So is the player, for a different reason: the phone is on
                        the floor or against a wall, and a mistap on the shell's
                        navigation mid-Tabata costs the user their workout. */}
                    <Route path={routes.playerPattern} element={<PlayerPage />} />

                    <Route element={<AppLayout />}>
                        {/* Everything below the shell first checks that the
                            intake is finished; without a profile these screens
                            would have nothing truthful to show. */}
                        <Route element={<RequireOnboarding />}>
                            <Route path={routes.dashboard} element={<DashboardPage />} />
                            {/*
                                The three levels of the training hierarchy, each
                                its own tab in the bar since the owner asked for
                                them there. `TrainLayout` used to wrap these three
                                with a rail that chose between them; with the
                                choice in the bar the rail was the same control
                                twice. See the second postscript on ADR 0018.
                            */}
                            <Route path={routes.programmes} element={<ProgrammesPage />} />
                            <Route path={routes.workouts} element={<WorkoutsPage />} />
                            <Route path={routes.exercises} element={<ExercisesPage />} />

                            <Route
                                path={routes.programmeBuilder}
                                element={<ProgrammeBuilderPage />}
                            />
                            <Route
                                path={routes.programmeEditorPattern}
                                element={<ProgrammeBuilderPage />}
                            />
                            <Route
                                path={routes.programmeDetailPattern}
                                element={<ProgrammeDetailPage />}
                            />
                            <Route path={routes.workoutBuilder} element={<BuilderPage />} />
                            <Route path={routes.workoutEditorPattern} element={<BuilderPage />} />
                            <Route
                                path={routes.workoutDetailPattern}
                                element={<WorkoutDetailPage />}
                            />
                            <Route
                                path={routes.exerciseDetailPattern}
                                element={<ExerciseDetailPage />}
                            />
                            <Route
                                path={routes.sessionSummaryPattern}
                                element={<SessionSummaryPage />}
                            />

                            {/*
                                Everything that belongs to the reader, under one
                                menu. A pathless layout route, so none of these
                                URLs moved — see AccountLayout.
                            */}
                            <Route element={<AccountLayout />}>
                                <Route path={routes.settings} element={<SettingsPage />} />
                                <Route path={routes.progress} element={<ProgressPage />} />
                                <Route path={routes.history} element={<HistoryPage />} />
                                <Route path={routes.measurements} element={<MeasurementsPage />} />
                                <Route path={routes.equipment} element={<EquipmentPage />} />
                                <Route
                                    path={routes.settingsReminders}
                                    element={<RemindersSettingsPage />}
                                />
                                <Route
                                    path={routes.settingsIntegrations}
                                    element={<IntegrationsSettingsPage />}
                                />
                            </Route>
                        </Route>

                        <Route path="*" element={<NotFoundPage />} />
                    </Route>
                </Route>
            </Routes>
        </Suspense>
    );
}
